import { z } from 'zod/v4';

export const PATIENT_ANNOUNCEMENT_BANNER_CONFIG_ID = 'patient_announcement_banner';
export const PATIENT_ANNOUNCEMENT_BANNER_STATSIG_URL =
  'https://console.statsig.com/6RFekk7eSu2Gu59L5p04xx/dynamic_configs/patient_announcement_banner';

// When a rule splits traffic across variants, Dev Console writes into this variant and moves it to 100%
// so the hand-maintained variants in Statsig stay intact.
export const PATIENT_ANNOUNCEMENT_BANNER_DEV_CONSOLE_VARIANT_NAME = 'Dev Console';

// Must stay a subset of BannerColorStyles in mobile-client/src/components/ui/Banner.tsx.
export const PATIENT_ANNOUNCEMENT_BANNER_COLORS = ['error', 'info', 'neutral', 'positive', 'ai'] as const;
export type PatientAnnouncementBannerColor = (typeof PATIENT_ANNOUNCEMENT_BANNER_COLORS)[number];

// Must stay a subset of CustomIconNames in mobile-client/src/components/ui/CustomIcon.tsx.
export const PATIENT_ANNOUNCEMENT_BANNER_ICONS = [
  'information-circle',
  'alert-circle',
  'warning-outline',
  'megaphone-outline',
] as const;
export type PatientAnnouncementBannerIcon = (typeof PATIENT_ANNOUNCEMENT_BANNER_ICONS)[number];

export const PATIENT_ANNOUNCEMENT_BANNER_MAX_TITLE_LENGTH = 120;
export const PATIENT_ANNOUNCEMENT_BANNER_MAX_TEXT_LENGTH = 500;

export const patientAnnouncementBannerSchema = z.object({
  title: z.string().trim().max(PATIENT_ANNOUNCEMENT_BANNER_MAX_TITLE_LENGTH).optional(),
  text: z.string().trim().min(1, 'Banner text is required').max(PATIENT_ANNOUNCEMENT_BANNER_MAX_TEXT_LENGTH),
  color: z.enum(PATIENT_ANNOUNCEMENT_BANNER_COLORS),
  icon: z.enum(PATIENT_ANNOUNCEMENT_BANNER_ICONS),
});

export type PatientAnnouncementBanner = z.infer<typeof patientAnnouncementBannerSchema>;

export const patientAnnouncementBannerUpdateRequestSchema = z.object({
  ruleId: z.string().trim().min(1, 'ruleId is required'),
  // null turns the banner off for the rule.
  banner: patientAnnouncementBannerSchema.nullable(),
});

export type PatientAnnouncementBannerUpdateRequest = z.infer<typeof patientAnnouncementBannerUpdateRequestSchema>;

export interface PatientAnnouncementBannerPreset {
  label: string;
  banner: PatientAnnouncementBanner;
}

export const PATIENT_ANNOUNCEMENT_BANNER_PRESETS: readonly PatientAnnouncementBannerPreset[] = [
  {
    label: 'OpenAI outage',
    banner: {
      text: "We're currently experiencing some issues. Some features like macro generation or chat may be impacted.",
      color: 'error',
      icon: 'information-circle',
    },
  },
  {
    label: 'AI chat outage',
    banner: {
      text: "Chat is temporarily unavailable while we work on a fix. We're sorry for the inconvenience and will have it back soon.",
      color: 'error',
      icon: 'information-circle',
    },
  },
  {
    label: 'Switch provider issue',
    banner: {
      text: "We're currently experiencing issues with switching providers. Please try again later.",
      color: 'error',
      icon: 'information-circle',
    },
  },
  {
    label: 'Scheduling issue',
    banner: {
      text: "We're currently experiencing issues with appointment scheduling. Your existing appointments are not affected.",
      color: 'error',
      icon: 'information-circle',
    },
  },
  {
    label: 'General outage',
    banner: {
      text: "We're currently experiencing some issues and are working on a fix. Some features may be unavailable.",
      color: 'error',
      icon: 'information-circle',
    },
  },
];

export interface PatientAnnouncementBannerVariant {
  id: string | null;
  name: string;
  passPercentage: number;
  value: Record<string, unknown> | null;
}

export interface PatientAnnouncementBannerRule {
  id: string;
  name: string;
  passPercentage: number;
  environments: string[] | null;
  // The rule's return value when it does not split into variants.
  value: Record<string, unknown> | null;
  variants: PatientAnnouncementBannerVariant[];
}

export interface PatientAnnouncementBannerConfigResponse {
  configId: string;
  statsigUrl: string;
  isEnabled: boolean;
  defaultValue: Record<string, unknown> | null;
  rules: PatientAnnouncementBannerRule[];
  lastModifiedTime: number | null;
  lastModifierName: string | null;
}

export interface PatientAnnouncementBannerUpdateResponse {
  // null when Statsig applied the update but re-reading the config failed; the client should refetch.
  config: PatientAnnouncementBannerConfigResponse | null;
}

/**
 * The value a fully-matched user receives from a rule: the return value for a plain rule, or the
 * variant at 100% for a split rule. Returns `undefined` when traffic is split across variants.
 */
export function getPatientAnnouncementBannerRuleValue(
  rule: PatientAnnouncementBannerRule
): Record<string, unknown> | null | undefined {
  if (rule.variants.length === 0) {
    return rule.value;
  }

  const activeVariant = rule.variants.find((variant) => variant.passPercentage === 100);
  return activeVariant == null ? undefined : activeVariant.value;
}

export function isPatientAnnouncementBannerValueVisible(value: Record<string, unknown> | null | undefined): boolean {
  return value != null && Object.keys(value).length > 0;
}
